import type { Column } from '~shared/rundown/column'
import type { Cue } from '~shared/rundown/cue'
import type { Cell } from '~shared/types'
import type { CellContent, CellSerialized } from '~shared/types/Cell'
import { cellFromSerialized } from '~shared/types/Cell'

import { rundownApi } from '../../../axios'
import { apiErrorHandler } from '../../../utils/api-error-handler'
import * as logger from '../../../utils/logger'
import { rundownPageStore } from '../rundown-page.store'

async function updateCell(
  rundownId: string,
  cellId: string,
  payload: Partial<Cell>,
) {
  return await apiErrorHandler(
    rundownApi.patch<CellSerialized>(`/${rundownId}/cells/${cellId}`, payload),
    // No API toast: callers show their own (`grid.save`, upload, delete contents)
    { toast: false },
  )
}

async function createCell(rundownId: string, payload: Partial<Cell>) {
  return await apiErrorHandler(
    rundownApi.post<CellSerialized>(`/${rundownId}/cells`, payload),
  )
}

/**
 * Updates the cell, or creates it if it doesn't exist yet.
 *
 * @param options.lastSeenUpdatedAt Overrides `cell.updatedAt` as the optimistic
 *   locking precondition, for when the client knows a newer version than the
 *   listener store holds (its own confirmed save).
 *
 * @returns The saved cell, as stored by the server
 */
export async function upsertCell(
  cell: Cell | undefined,
  columnId: Column['id'],
  cueId: Cue['id'],
  contentPayload: CellContent,
  options: { lastSeenUpdatedAt?: Date } = {},
): Promise<Cell> {
  const {
    rundown: { id: rundownId },
  } = rundownPageStore

  const payload = contentPayload || {}

  logger.log('[Cell] upsertCell. Payload:', payload)

  // Cell exists
  if (cell?.id) {
    logger.log('[Cell] updateCell', cell.id)

    // Echo the cell's last-known `updatedAt` back to the server as the
    // optimistic-locking precondition. Server rejects with 409 if the
    // stored cell has moved on since the client received this snapshot.
    const lastSeenUpdatedAt = options.lastSeenUpdatedAt ?? cell.updatedAt
    try {
      const { data } = await updateCell(rundownId, cell.id, {
        ...payload,
        updatedAt: lastSeenUpdatedAt,
      })

      return cellFromSerialized(data)
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      if (status === 409) {
        logger.warn('[Cell] save-rejected-409', {
          cellId: cell.id,
          lastSeenUpdatedAt:
            lastSeenUpdatedAt instanceof Date
              ? lastSeenUpdatedAt.getTime()
              : null,
        })
      }

      throw err
      // Error already toasted upstream
    }
  }

  // No cell exists, create new
  else {
    logger.log('[Cell] createCell')

    const { data } = await createCell(rundownId, {
      ...payload,
      columnId,
      cueId,
    })
    return cellFromSerialized(data)
  }
}
